/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useState, useEffect } from 'react';

import { Box, Button, makeStyles, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip } from '@material-ui/core';
import { Add, Delete, OpenInNew } from '@material-ui/icons';
import moment from 'moment';

import AddObservationToQuizModal from '../../components/observation/AddObservationToQuizModal';
import { MODALITY_OPTIONS_LABELS } from '../../constants/observations';
import QuizCollectionService from '../../services/quizCollection.service';

const useStyle = makeStyles((theme) => {
  return {
    addButton: {
      width: 102,
    },
    deleteIcon: {
      color: theme.palette.grey['500'],
      cursor: 'pointer',
      fontSize: '20px',
    },
    linkIconButton: {
      color: '#2AAAE2',
      padding: 4,
    },
    sectionContainer: {
      marginBottom: '32px',
      width: '100%',
    },
    sectionDescription: {
      color: '#999999',
      fontSize: '12px',
      marginBottom: 4,
      textAlign: 'left',
    },
    sectionHeader: {
      fontSize: '16px',
      fontWeight: '600',
    },
    table: {
      minWidth: 650,
    },
    tableCell: {
      paddingBottom: '8px !important',
      paddingTop: '8px !important',
    },
    tableContainer: {
      marginTop: 16,
      maxHeight: 400,
    },
    tableHeader: {
      borderBottom: '1px solid #EBEBEB',
    },
    tableHeaderCell: {
      backgroundColor: '#FFF',
      borderBottom: '1px solid #EBEBEB',
      fontSize: 12,
      fontWeight: 'bold',
    },
  };
});

const QuizObservations = ({ quizCollection, isLoading, onObservationsChange }) => {
  const classes = useStyle();
  const [observations, setObservations] = useState([]);
  const [clinicalCaseModalOpen, setClinicalCaseModalOpen] = useState(false);
  const quizCollectionService = new QuizCollectionService();

  const fetchObservations = useCallback(async () => {
    if (!quizCollection?.id) return;

    try {
      const response = await quizCollectionService.getQuizCollectionObservations(quizCollection.id);
      setObservations(response.observations || []);
    } catch (error) {
      console.error('Error fetching quiz observations:', error);
      setObservations([]);
    }
  }, [quizCollection?.id]);

  useEffect(() => {
    fetchObservations();
  }, [fetchObservations]);

  useEffect(() => {
    if (onObservationsChange) {
      const observationIds = observations.map((obs) => obs.qualityObservationId || obs.id);
      onObservationsChange(observationIds);
    }
  }, [observations, onObservationsChange]);

  const handleAddClinicalCase = useCallback(() => {
    setClinicalCaseModalOpen(true);
  }, []);

  const handleCloseClinicalCaseModal = useCallback(() => {
    setClinicalCaseModalOpen(false);
  }, []);

  const handleAddSelectedObservations = useCallback((selectedObservations) => {
    const observationsWithId = selectedObservations.map((obs) => ({
      qualityObservationId: obs.qualityObservationId || obs.id,
      ...obs,
    }));

    setObservations(observationsWithId);
    setClinicalCaseModalOpen(false);
  }, []);

  const handleDeleteObservation = useCallback(
    (observation) => {
      setObservations(observations.filter((o) => o.id !== observation.id));
    },
    [observations]
  );

  return (
    <>
      <div className={classes.sectionContainer}>
        <Box display="flex" flexDirection="row" gridGap={32} justifyContent="space-between" alignItems="flex-start">
          <Box display="flex" flexDirection="column" gridGap={4} alignItems="flex-start">
            <Typography className={classes.sectionHeader}>Clinical Cases</Typography>
            <Typography className={classes.sectionDescription}>
              Add clinical cases to your quiz collection to provide context and help participants understand the context of the quiz.
            </Typography>
          </Box>

          <Button className={classes.addButton} variant="contained" color="primary" startIcon={<Add />} onClick={handleAddClinicalCase} disabled={isLoading}>
            Add
          </Button>
        </Box>

        {observations.length > 0 && (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table} stickyHeader>
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell className={classes.tableHeaderCell}>ID</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Date</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Exam</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Facility</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Modality</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Educ.Value</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Severity</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Frequency</TableCell>
                  <TableCell className={classes.tableHeaderCell} width="40px" />
                  <TableCell className={classes.tableHeaderCell} width="40px" />
                </TableRow>
              </TableHead>

              <TableBody>
                {observations.map((observation) => (
                  <TableRow key={observation.id || observation.qualityObservationId} hover>
                    <TableCell className={classes.tableCell} width="50px">
                      {observation.qualityObservationId || observation.id || '-'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>{observation.createdAt ? moment(observation.createdAt).format('MM/DD/YY') : '-'}</TableCell>
                    <TableCell className={classes.tableCell}>{observation.examName || '-'}</TableCell>
                    <TableCell className={classes.tableCell}>{observation.facility?.name || observation.facility || '-'}</TableCell>
                    <TableCell className={classes.tableCell}>{MODALITY_OPTIONS_LABELS[observation.modality] || observation.modality || '-'}</TableCell>
                    <TableCell className={classes.tableCell} width="40px" align="center">
                      {observation.scores?.educationalValue || '-'}
                    </TableCell>
                    <TableCell className={classes.tableCell} width="40px" align="center">
                      {observation.scores?.severityOfIssue || '-'}
                    </TableCell>
                    <TableCell className={classes.tableCell} width="40px" align="center">
                      {observation.scores?.frequencyOfIssue || '-'}
                    </TableCell>
                    <TableCell className={classes.tableCell} width="40px" align="center">
                      <Tooltip title="Open in new tab">
                        <IconButton
                          className={classes.linkIconButton}
                          size="small"
                          onClick={() => window.open(`/observation/${observation.qualityObservationId || observation.id}`, '_blank')}>
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell className={classes.tableCell} width="40px" align="center">
                      <IconButton size="small" onClick={() => handleDeleteObservation(observation)}>
                        <Delete className={classes.deleteIcon} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>

      <AddObservationToQuizModal
        open={clinicalCaseModalOpen}
        onClose={handleCloseClinicalCaseModal}
        onAddSelectedObservations={handleAddSelectedObservations}
        existingObservations={observations}
      />
    </>
  );
};

export default QuizObservations;
